import type {
	APIBlogCategoriesBrowseQueryParams,
	APIBlogCategoriesBrowseResult,
	APIBlogCategoryGetByIdParams,
	APIBlogCategoryGetByIdResult,
	APICartCreateBody,
	APICartCreateResult,
	APICartGetResult,
	APICategoriesBrowseQueryParams,
	APICategoriesBrowseResult,
	APICategoryCreateBody,
	APICategoryCreateResult,
	APICategoryGetByIdParams,
	APICategoryGetByIdResult,
	APICategoryUpdateBody,
	APICategoryUpdateResult,
	APICollectionCreateBody,
	APICollectionCreateResult,
	APICollectionGetByIdParams,
	APICollectionGetByIdResult,
	APICollectionsBrowseQueryParams,
	APICollectionsBrowseResult,
	APIContactMessageCreateBody,
	APIContactMessageCreateResult,
	APICustomerAddressCreateBody,
	APICustomerAddressCreateResult,
	APICustomerGetByIdParams,
	APICustomerGetByIdResult,
	APICustomerOrdersBrowseQueryParams,
	APICustomerOrdersBrowseResult,
	APICustomersBrowseQueryParams,
	APICustomersBrowseResult,
	APICustomerUpdateBody,
	APICustomerUpdateResult,
	APIInstaviewImagesBrowseParams,
	APIInstaviewImagesBrowseQueryParams,
	APIInstaviewImagesBrowseResult,
	APIInventoryAdjustBody,
	APIInventoryAdjustResult,
	APIInventoryBrowseQueryParams,
	APIInventoryBrowseResult,
	APILegalPageGetByPathResult,
	APILegalPagesBrowseResult,
	APIMeGetResult,
	APIOrderGetByIdParams,
	APIOrderGetByIdResult,
	APIOrdersBrowseQueryParams,
	APIOrdersBrowseResult,
	APIOrderUpdateBody,
	APIOrderUpdateResult,
	APIPostCommentCreateBody,
	APIPostCommentCreateResult,
	APIPostCommentsBrowseQueryParams,
	APIPostCommentsBrowseResult,
	APIPostCreateBody,
	APIPostCreateResult,
	APIPostDeleteResult,
	APIPostGetByIdParams,
	APIPostGetByIdResult,
	APIPostsBrowseQueryParams,
	APIPostsBrowseResult,
	APIPostUpdateBody,
	APIPostUpdateResult,
	APIProductCreateBody,
	APIProductCreateResult,
	APIProductDeleteResult,
	APIProductGetByIdParams,
	APIProductGetByIdResult,
	APIProductReviewCreateBody,
	APIProductReviewCreateResult,
	APIProductReviewsBrowseQueryParams,
	APIProductReviewsBrowseResult,
	APIProductsBrowseQueryParams,
	APIProductsBrowseResult,
	APIProductUpdateBody,
	APIProductUpdateResult,
	APISearchQueryParams,
	APISearchResult,
	APISubscriberCreateBody,
	APISubscriberCreateResult,
	APISubscriberDeleteResult,
	APIVariantCreateBody,
	APIVariantCreateResult,
	APIVariantGetByIdParams,
	APIVariantGetByIdResult,
	APIVariantUpdateBody,
	APIVariantUpdateResult,
} from "../api-types";
import { env } from "../env";
import { getLogger } from "../logger";
import type { CommerceConfig } from "../types";

export type APICollectionImportMembershipsBody = { file: File | Blob };

export type APICollectionImportMembershipsResult = {
	ok: true;
	processed: number;
	inserted: number;
	skipped_existing: number;
	errors: Array<{
		row: number;
		collection_slug?: string;
		product_slug?: string;
		reason: string;
	}>;
};

export class YNSProvider {
	#config: CommerceConfig;
	#logger = getLogger("YNSProvider");

	constructor(config: CommerceConfig = {}) {
		const token = config.token ?? env.YNS_API_KEY;
		const isStaging = env.YNS_API_KEY?.startsWith("sk-s-");

		if (!token) {
			throw new Error(
				"YNS API key is required. Set YNS_API_KEY environment variable or pass token in config.",
			);
		}
		const endpoint = config.endpoint ?? (isStaging ? "https://yns.cx" : "https://yns.store");

		this.#config = { version: "v1", ...config, token, endpoint };

		this.#logger.debug("YNSProvider initialized", {
			endpoint,
			token: config.token ? `from config` : "from env",
		});
	}

	async #restRequest<T>(
		pathname: `/${string}`,
		method: "GET" | "POST" | "PATCH" | "DELETE" = "GET",
		body?: unknown,
	): Promise<T> {
		const logger = this.#logger.getLogger("#restRequest");

		const endpoint = `${this.#config.endpoint}/api/${this.#config.version}${pathname}`;
		logger.debug(`Making ${method} request to YNS API: ${endpoint}`);
		const response = await fetch(endpoint, {
			method,
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${this.#config.token}`,
			},
			body: body ? JSON.stringify(body) : undefined,
		});

		if (!response.ok) {
			// Handle different error types
			const contentType = response.headers.get("content-type");
			let errorMessage = `YNS REST request failed: ${method} ${endpoint} ${response.status} ${response.statusText}`;

			if (contentType?.includes("application/json")) {
				try {
					const errorData = await response.json();
					errorMessage = errorData.error || errorData.message || errorMessage;
				} catch (error) {
					logger.error("Failed to parse YNS error response as JSON", error);
				}
			} else {
				const errorText = await response.text();
				logger.error(
					`YNS API request failed: ${response.status} ${response.statusText}`,
					errorMessage,
					errorText,
				);
			}

			throw new Error(errorMessage);
		}

		// Check if response is JSON before parsing
		const contentType = response.headers.get("content-type");
		if (!contentType?.includes("application/json")) {
			throw new Error(`YNS API returned ${contentType} instead of JSON for ${pathname}`);
		}

		return response.json();
	}

	async #multipartRequest<T>(pathname: `/${string}`, formData: FormData): Promise<T> {
		const logger = this.#logger.getLogger("#multipartRequest");

		const endpoint = `${this.#config.endpoint}/api/${this.#config.version}${pathname}`;
		logger.debug(`Making multipart POST to YNS API: ${endpoint}`);
		const response = await fetch(endpoint, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${this.#config.token}`,
			},
			body: formData,
		});

		if (!response.ok) {
			const contentType = response.headers.get("content-type");
			let errorMessage = `YNS REST request failed: POST ${endpoint} ${response.status} ${response.statusText}`;

			if (contentType?.includes("application/json")) {
				try {
					const errorData = await response.json();
					errorMessage = errorData.error || errorData.message || errorMessage;
				} catch (error) {
					logger.error("Failed to parse YNS error response as JSON", error);
				}
			} else {
				const errorText = await response.text();
				logger.error(
					`YNS API request failed: ${response.status} ${response.statusText}`,
					errorMessage,
					errorText,
				);
			}

			throw new Error(errorMessage);
		}

		const contentType = response.headers.get("content-type");
		if (!contentType?.includes("application/json")) {
			throw new Error(`YNS API returned ${contentType} instead of JSON for ${pathname}`);
		}

		return response.json();
	}

	async meGet(): Promise<APIMeGetResult> {
		const logger = this.#logger.getLogger("meGet");
		logger.debug("Fetching my information");

		const pathname = `/me` as const;
		const result = await this.#restRequest<APIMeGetResult>(pathname);
		logger.debug("Received my information:", result);
		return result;
	}

	async productBrowse(params: APIProductsBrowseQueryParams): Promise<APIProductsBrowseResult> {
		const logger = this.#logger.getLogger("productBrowse");
		logger.debug("Browsing products with params:", params);

		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.category) queryParams.append("category", params.category);
		if (params.query) queryParams.append("query", params.query);
		if (params.active !== undefined) queryParams.append("active", params.active.toString());
		if (params.orderBy) queryParams.append("orderBy", params.orderBy);
		if (params.orderDirection) queryParams.append("orderDirection", params.orderDirection);

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/products${searchParams}` as const;
		logger.debug("Constructed pathname:", pathname);
		const result = await this.#restRequest<APIProductsBrowseResult>(pathname);
		logger.debug("Received product browse result:", { meta: result.meta });
		return result;
	}

	async productGet(params: APIProductGetByIdParams): Promise<APIProductGetByIdResult | null> {
		const pathname = `/products/${params.idOrSlug}` as const;

		const result = await this.#restRequest<APIProductGetByIdResult>(pathname);

		if (!result) {
			return null;
		}

		return result;
	}

	async orderBrowse(params: APIOrdersBrowseQueryParams): Promise<APIOrdersBrowseResult> {
		const logger = this.#logger.getLogger("orderBrowse");
		logger.debug("Browsing orders with params:", params);

		const queryParams = new URLSearchParams();

		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/orders${searchParams}` as const;
		logger.debug("Constructed pathname:", pathname);
		const result = await this.#restRequest<APIOrdersBrowseResult>(pathname);
		logger.debug("Received orders browse result:", { meta: result.meta });
		return result;
	}

	async orderGet(params: APIOrderGetByIdParams): Promise<APIOrderGetByIdResult | null> {
		const pathname = `/orders/${params.id}` as const;

		const result = await this.#restRequest<APIOrderGetByIdResult>(pathname);

		if (!result) {
			return null;
		}

		return result;
	}

	// @todo
	// async cartAdd(params: APICartAddBody): Promise<APICartAddResult> {
	// 	const body = {
	// 		variantId: params.variantId,
	// 		cartId: params.cartId,
	// 		quantity: params.quantity,
	// 		subscriptionId: params.subscriptionId,
	// 	};

	// 	const result = await this.#restRequest<APICartCreateResult>("/carts", "PATCH", body);
	// 	return result;
	// }

	async cartUpsert(body: APICartCreateBody): Promise<APICartCreateResult> {
		const result = await this.#restRequest<APICartCreateResult>("/carts", "POST", body);
		return result;
	}

	async cartRemoveItem(params: { cartId: string; variantId: string }): Promise<APICartGetResult> {
		const pathname = `/carts/${params.cartId}/line-items/${params.variantId}` as const;
		return this.#restRequest<APICartGetResult>(pathname, "DELETE");
	}

	async cartGet(params: { cartId: string }): Promise<APICartGetResult | null> {
		const pathname = `/carts/${params.cartId}` as const;
		const result = await this.#restRequest<APICartGetResult>(pathname);
		return result;
	}

	async collectionGet(params: APICollectionGetByIdParams): Promise<APICollectionGetByIdResult | null> {
		const pathname = `/collections/${params.idOrSlug}` as const;

		const result = await this.#restRequest<APICollectionGetByIdResult>(pathname);
		return result;
	}

	async collectionBrowse(params: APICollectionsBrowseQueryParams): Promise<APICollectionsBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.query) queryParams.append("query", params.query);
		if (params.active !== undefined) queryParams.append("active", params.active.toString());

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/collections${searchParams}` as const;

		const result = await this.#restRequest<APICollectionsBrowseResult>(pathname);
		return result;
	}

	async collectionCreate(body: APICollectionCreateBody): Promise<APICollectionCreateResult> {
		return this.#restRequest<APICollectionCreateResult>("/collections", "POST", body);
	}

	async collectionImportMemberships(
		body: APICollectionImportMembershipsBody,
	): Promise<APICollectionImportMembershipsResult> {
		const formData = new FormData();
		formData.append("file", body.file);
		return this.#multipartRequest<APICollectionImportMembershipsResult>(
			"/collections/import-memberships",
			formData,
		);
	}

	async categoryGet(params: APICategoryGetByIdParams): Promise<APICategoryGetByIdResult | null> {
		const pathname = `/categories/${params.idOrSlug}` as const;

		const result = await this.#restRequest<APICategoryGetByIdResult>(pathname);
		return result;
	}

	async categoriesBrowse(params: APICategoriesBrowseQueryParams): Promise<APICategoriesBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.query) queryParams.append("query", params.query);
		if (params.active !== undefined) queryParams.append("active", params.active.toString());

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/categories${searchParams}` as const;

		const result = await this.#restRequest<APICategoriesBrowseResult>(pathname);
		return result;
	}

	// ============================================
	// Posts
	// ============================================

	async postBrowse(params: APIPostsBrowseQueryParams = {}): Promise<APIPostsBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.query) queryParams.append("query", params.query);
		if (params.active !== undefined) queryParams.append("active", params.active.toString());
		if (params.tag) queryParams.append("tag", params.tag);
		if (params.categoryId) queryParams.append("categoryId", params.categoryId);

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/posts${searchParams}` as const;

		return this.#restRequest<APIPostsBrowseResult>(pathname);
	}

	async postGet(params: APIPostGetByIdParams): Promise<APIPostGetByIdResult | null> {
		const pathname = `/posts/${params.idOrSlug}` as const;
		return this.#restRequest<APIPostGetByIdResult>(pathname);
	}

	async postCreate(body: APIPostCreateBody): Promise<APIPostCreateResult> {
		return this.#restRequest<APIPostCreateResult>("/posts", "POST", body);
	}

	async postUpdate(params: APIPostGetByIdParams, body: APIPostUpdateBody): Promise<APIPostUpdateResult> {
		const pathname = `/posts/${params.idOrSlug}` as const;
		return this.#restRequest<APIPostUpdateResult>(pathname, "PATCH", body);
	}

	async postDelete(params: APIPostGetByIdParams): Promise<APIPostDeleteResult> {
		const pathname = `/posts/${params.idOrSlug}` as const;
		return this.#restRequest<APIPostDeleteResult>(pathname, "DELETE");
	}

	async postCommentsBrowse(
		params: APIPostGetByIdParams,
		queryParams: APIPostCommentsBrowseQueryParams = {},
	): Promise<APIPostCommentsBrowseResult> {
		const urlParams = new URLSearchParams();
		if (queryParams.limit) urlParams.append("limit", queryParams.limit.toString());
		if (queryParams.offset) urlParams.append("offset", queryParams.offset.toString());

		const searchParams = urlParams.size ? `?${urlParams}` : "";
		const pathname = `/posts/${params.idOrSlug}/comments${searchParams}` as const;

		return this.#restRequest<APIPostCommentsBrowseResult>(pathname);
	}

	async postCommentCreate(
		params: APIPostGetByIdParams,
		body: APIPostCommentCreateBody,
	): Promise<APIPostCommentCreateResult> {
		const pathname = `/posts/${params.idOrSlug}/comments` as const;
		return this.#restRequest<APIPostCommentCreateResult>(pathname, "POST", body);
	}

	// ============================================
	// Blog Categories
	// ============================================

	async blogCategoryBrowse(
		params: APIBlogCategoriesBrowseQueryParams = {},
	): Promise<APIBlogCategoriesBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.query) queryParams.append("query", params.query);
		if (params.active !== undefined) queryParams.append("active", params.active.toString());

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/blog-categories${searchParams}` as const;

		return this.#restRequest<APIBlogCategoriesBrowseResult>(pathname);
	}

	async blogCategoryGet(params: APIBlogCategoryGetByIdParams): Promise<APIBlogCategoryGetByIdResult | null> {
		const pathname = `/blog-categories/${params.idOrSlug}` as const;
		return this.#restRequest<APIBlogCategoryGetByIdResult>(pathname);
	}

	// ============================================
	// Customers
	// ============================================

	async customerBrowse(params: APICustomersBrowseQueryParams = {}): Promise<APICustomersBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.offset) queryParams.append("offset", params.offset.toString());
		if (params.search) queryParams.append("search", params.search);

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/customers${searchParams}` as const;

		return this.#restRequest<APICustomersBrowseResult>(pathname);
	}

	async customerGet(params: APICustomerGetByIdParams): Promise<APICustomerGetByIdResult | null> {
		const pathname = `/customers/${params.id}` as const;
		return this.#restRequest<APICustomerGetByIdResult>(pathname);
	}

	async customerUpdate(
		params: APICustomerGetByIdParams,
		body: APICustomerUpdateBody,
	): Promise<APICustomerUpdateResult> {
		const pathname = `/customers/${params.id}` as const;
		return this.#restRequest<APICustomerUpdateResult>(pathname, "PATCH", body);
	}

	async customerAddressCreate(
		params: APICustomerGetByIdParams,
		body: APICustomerAddressCreateBody,
	): Promise<APICustomerAddressCreateResult> {
		const pathname = `/customers/${params.id}/addresses` as const;
		return this.#restRequest<APICustomerAddressCreateResult>(pathname, "POST", body);
	}

	async customerAddressDelete(params: { customerId: string; addressId: string }): Promise<void> {
		const pathname = `/customers/${params.customerId}/addresses/${params.addressId}` as const;
		await this.#restRequest<unknown>(pathname, "DELETE");
	}

	async customerOrdersBrowse(
		params: APICustomerGetByIdParams,
		queryParams: APICustomerOrdersBrowseQueryParams = {},
	): Promise<APICustomerOrdersBrowseResult> {
		const urlParams = new URLSearchParams();
		if (queryParams.limit) urlParams.append("limit", queryParams.limit.toString());
		if (queryParams.offset) urlParams.append("offset", queryParams.offset.toString());

		const searchParams = urlParams.size ? `?${urlParams}` : "";
		const pathname = `/customers/${params.id}/orders${searchParams}` as const;

		return this.#restRequest<APICustomerOrdersBrowseResult>(pathname);
	}

	// ============================================
	// Inventory
	// ============================================

	async inventoryBrowse(params: APIInventoryBrowseQueryParams = {}): Promise<APIInventoryBrowseResult> {
		const queryParams = new URLSearchParams();
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.cursor) queryParams.append("cursor", params.cursor);
		if (params.lowStock !== undefined) queryParams.append("lowStock", params.lowStock.toString());

		const searchParams = queryParams.size ? `?${queryParams}` : "";
		const pathname = `/inventory${searchParams}` as const;

		return this.#restRequest<APIInventoryBrowseResult>(pathname);
	}

	async inventoryAdjust(sku: string, body: APIInventoryAdjustBody): Promise<APIInventoryAdjustResult> {
		const pathname = `/inventory/${encodeURIComponent(sku)}/adjust` as const;
		return this.#restRequest<APIInventoryAdjustResult>(pathname, "POST", body);
	}

	// ============================================
	// Variants
	// ============================================

	async variantGet(params: APIVariantGetByIdParams): Promise<APIVariantGetByIdResult | null> {
		const pathname = `/variants/${params.idOrSku}` as const;
		return this.#restRequest<APIVariantGetByIdResult>(pathname);
	}

	async variantUpdate(
		params: APIVariantGetByIdParams,
		body: APIVariantUpdateBody,
	): Promise<APIVariantUpdateResult> {
		const pathname = `/variants/${params.idOrSku}` as const;
		return this.#restRequest<APIVariantUpdateResult>(pathname, "PATCH", body);
	}

	async variantDelete(params: APIVariantGetByIdParams): Promise<void> {
		const pathname = `/variants/${params.idOrSku}` as const;
		await this.#restRequest<unknown>(pathname, "DELETE");
	}

	async variantCreate(productId: string, body: APIVariantCreateBody): Promise<APIVariantCreateResult> {
		const pathname = `/products/${productId}/variants` as const;
		return this.#restRequest<APIVariantCreateResult>(pathname, "POST", body);
	}

	// ============================================
	// Subscribers
	// ============================================

	async subscriberCreate(body: APISubscriberCreateBody): Promise<APISubscriberCreateResult> {
		return this.#restRequest<APISubscriberCreateResult>("/subscribers", "POST", body);
	}

	async subscriberDelete(email: string): Promise<APISubscriberDeleteResult> {
		const pathname = `/subscribers?email=${encodeURIComponent(email)}` as const;
		return this.#restRequest<APISubscriberDeleteResult>(pathname, "DELETE");
	}

	// ============================================
	// Contact Messages
	// ============================================

	async contactMessageCreate(body: APIContactMessageCreateBody): Promise<APIContactMessageCreateResult> {
		return this.#restRequest<APIContactMessageCreateResult>("/contact-messages", "POST", body);
	}

	// ============================================
	// Product mutations
	// ============================================

	async productCreate(body: APIProductCreateBody): Promise<APIProductCreateResult> {
		return this.#restRequest<APIProductCreateResult>("/products", "POST", body);
	}

	async productUpdate(
		params: APIProductGetByIdParams,
		body: APIProductUpdateBody,
	): Promise<APIProductUpdateResult> {
		const pathname = `/products/${params.idOrSlug}` as const;
		return this.#restRequest<APIProductUpdateResult>(pathname, "PATCH", body);
	}

	async productDelete(params: APIProductGetByIdParams): Promise<APIProductDeleteResult> {
		const pathname = `/products/${params.idOrSlug}` as const;
		return this.#restRequest<APIProductDeleteResult>(pathname, "DELETE");
	}

	// ============================================
	// Product Reviews
	// ============================================

	async productReviewsBrowse(
		params: APIProductGetByIdParams,
		queryParams: APIProductReviewsBrowseQueryParams = {},
	): Promise<APIProductReviewsBrowseResult> {
		const urlParams = new URLSearchParams();
		if (queryParams.limit) urlParams.append("limit", queryParams.limit.toString());
		if (queryParams.offset) urlParams.append("offset", queryParams.offset.toString());

		const searchParams = urlParams.size ? `?${urlParams}` : "";
		const pathname = `/products/${params.idOrSlug}/reviews${searchParams}` as const;

		return this.#restRequest<APIProductReviewsBrowseResult>(pathname);
	}

	async productReviewCreate(
		params: APIProductGetByIdParams,
		body: APIProductReviewCreateBody,
	): Promise<APIProductReviewCreateResult> {
		const pathname = `/products/${params.idOrSlug}/reviews` as const;
		return this.#restRequest<APIProductReviewCreateResult>(pathname, "POST", body);
	}

	// ============================================
	// Category mutations
	// ============================================

	async categoryCreate(body: APICategoryCreateBody): Promise<APICategoryCreateResult> {
		return this.#restRequest<APICategoryCreateResult>("/categories", "POST", body);
	}

	async categoryUpdate(
		params: APICategoryGetByIdParams,
		body: APICategoryUpdateBody,
	): Promise<APICategoryUpdateResult> {
		const pathname = `/categories/${params.idOrSlug}` as const;
		return this.#restRequest<APICategoryUpdateResult>(pathname, "PATCH", body);
	}

	// ============================================
	// Order mutations
	// ============================================

	async orderUpdate(params: APIOrderGetByIdParams, body: APIOrderUpdateBody): Promise<APIOrderUpdateResult> {
		const pathname = `/orders/${params.id}` as const;
		return this.#restRequest<APIOrderUpdateResult>(pathname, "PATCH", body);
	}

	// ============================================
	// Legal Pages
	// ============================================

	async legalPageBrowse(): Promise<APILegalPagesBrowseResult> {
		return this.#restRequest<APILegalPagesBrowseResult>("/legal-pages");
	}

	async legalPageGet(path: string): Promise<APILegalPageGetByPathResult> {
		const normalized = path.startsWith("/") ? path.slice(1) : path;
		const pathname = `/legal-pages/${encodeURIComponent(normalized)}` as const;
		return this.#restRequest<APILegalPageGetByPathResult>(pathname);
	}

	async search(params: APISearchQueryParams): Promise<APISearchResult> {
		return this.request<APISearchResult>("/search", {
			query: params,
		});
	}

	// ============================================
	// Instaview
	// ============================================

	async instaviewImagesBrowse(
		params: APIInstaviewImagesBrowseParams,
		queryParams: APIInstaviewImagesBrowseQueryParams = {},
	): Promise<APIInstaviewImagesBrowseResult> {
		const urlParams = new URLSearchParams();
		if (queryParams.limit) urlParams.append("limit", queryParams.limit.toString());
		if (queryParams.cursor) urlParams.append("cursor", queryParams.cursor);

		const searchParams = urlParams.size ? `?${urlParams}` : "";
		const pathname = `/instaview/accounts/${params.handle}/images${searchParams}` as const;

		return this.#restRequest<APIInstaviewImagesBrowseResult>(pathname);
	}

	/**
	 * Make a raw API request to any endpoint.
	 * Use this for new API features not yet supported by typed methods.
	 *
	 * @example
	 * // GET request (default method)
	 * const webhooks = await provider.request<Webhook[]>('/webhooks');
	 *
	 * // POST with typed body
	 * const webhook = await provider.request<Webhook, CreateWebhookBody>('/webhooks', {
	 *   method: 'POST',
	 *   body: { url: 'https://...' }
	 * });
	 *
	 * // GET with query parameters
	 * const products = await provider.request<Product[]>('/products', {
	 *   query: { limit: 10, category: 'shoes' }
	 * });
	 *
	 * // Path parameters via template literals
	 * const product = await provider.request<Product>(`/products/${id}`);
	 */
	async request<TResponse = unknown, TBody = unknown>(
		pathname: `/${string}`,
		options?: {
			method?: "GET" | "POST" | "PATCH" | "DELETE";
			body?: TBody;
			query?: Record<string, string | number | boolean>;
		},
	) {
		const queryString = options?.query
			? `?${new URLSearchParams(Object.entries(options.query).map(([k, v]) => [k, String(v)]))}`
			: "";

		return this.#restRequest<TResponse>(`${pathname}${queryString}`, options?.method ?? "GET", options?.body);
	}
}
